import { Component } from '@theme/component';

/**
 * Echt · Model info.
 *
 * The "MODEL INFO" pill over the first gallery image, and the two ways it can
 * answer (Basil, 7 Sep 2026, from the SET Active reference: "I also want this
 * Model Info size picker on popup as well on mobile").
 *
 * - From 750px up the sentence is revealed in place, over the foot of the image
 *   it describes. There is room for it there and a modal for one line of copy on
 *   a desktop viewport is a heavier gesture than the content deserves.
 * - Below 750px it opens a native `<dialog>` as a bottom sheet. Native, so the
 *   focus trap, Escape, the inertness of the page behind and the backdrop are
 *   the browser's rather than ours — the same call echt-size-guide made.
 *
 * 750px, not `--bp-md`: it is the breakpoint the gallery's own full-bleed rule
 * already uses, and stock Horizon's. Kept in one constant here and in one media
 * query in the block, so the two cannot drift far without a test noticing.
 *
 * With JavaScript off the pill does nothing and the caption stays hidden — a
 * detail on a product page, never the purchase path.
 *
 * @extends Component
 */
export default class EchtModelInfo extends Component {
  /** The sentence has to be somewhere for the pill to be worth pressing. */
  requiredRefs = ['trigger', 'line', 'sheet'];

  /** @type {MediaQueryList | null} */
  #desktop = null;

  #pinned = false;

  connectedCallback() {
    super.connectedCallback();

    this.#desktop = window.matchMedia('(min-width: 750px)');

    this.#syncSemantics();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerout', this.#onPointerOut);
    // A resize across the breakpoint would otherwise leave the sheet open on a
    // desktop viewport, where the caption is the affordance and the sheet is
    // not styled for the width.
    this.#desktop.addEventListener('change', this.#onBreakpoint);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('pointerover', this.#onPointerOver);
    this.removeEventListener('pointerout', this.#onPointerOut);
    this.#desktop?.removeEventListener('change', this.#onBreakpoint);
    this.#desktop = null;
  }

  /**
   * One delegated listener rather than one per control: the close button and
   * the backdrop are both inside this element, and delegation survives the
   * caption text being replaced.
   *
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    if (!target) return;

    const { trigger, sheet, close } = this.#parts();

    if (trigger && target.closest('.echt-gal__modelpill')) {
      event.preventDefault();
      this.#toggle();
      return;
    }

    if (close && target.closest('.echt-gal__modelclose')) {
      sheet?.close();
      return;
    }

    // A hit on the dialog element itself is a hit on its backdrop: the panel is
    // the only thing painted inside it.
    if (sheet && target === sheet) sheet.close();
  };

  #onBreakpoint = () => {
    const { sheet } = this.#parts();
    if (this.#desktop?.matches) sheet?.close();
    else this.#collapse();
    this.#syncSemantics();
  };

  // Hover previews the caption. Clicking pins it independently of hover, so
  // the first click keeps an already visible preview open.
  #onPointerOver = (event) => {
    if (!this.#desktop?.matches || event.pointerType === 'touch') return;
    const group = event.target.closest?.('.echt-gal__modelgroup');
    if (!group || group.contains(event.relatedTarget)) return;
    this.#reveal(true);
  };

  #onPointerOut = (event) => {
    if (!this.#desktop?.matches || event.pointerType === 'touch' || this.#pinned) return;
    const group = event.target.closest?.('.echt-gal__modelgroup');
    if (!group || group.contains(event.relatedTarget)) return;
    this.#reveal(false);
  };

  #reveal(open) {
    const { trigger, line } = this.#parts();
    if (!trigger || !line) return;
    line.hidden = !open;
    trigger.setAttribute('aria-expanded', String(open));
  }

  /**
   * The same button does two different things, and it has to say which. Above
   * 750 it expands a caption that is in the DOM already (aria-expanded, and the
   * aria-controls the markup carries); below it, it summons a dialog, where
   * aria-expanded would be a lie and aria-haspopup is the truth.
   */
  #syncSemantics() {
    const { trigger } = this.#parts();
    if (!trigger) return;

    if (this.#desktop?.matches) {
      trigger.removeAttribute('aria-haspopup');
      trigger.setAttribute('aria-expanded', String(this.#parts().line?.hidden === false));
    } else {
      trigger.removeAttribute('aria-expanded');
      trigger.setAttribute('aria-haspopup', 'dialog');
    }
  }

  #toggle() {
    const { trigger, line, sheet } = this.#parts();
    if (!trigger || !line) return;

    if (this.#desktop?.matches) {
      this.#pinned = !this.#pinned;
      this.#reveal(this.#pinned);
      return;
    }

    if (!sheet) return;
    // Filled from the caption, so the metafield (or the test hook that stands in
    // for it) is the one source of the sentence and the sheet can never show a
    // stale copy of it.
    const { sheetText } = this.#parts();
    if (sheetText) sheetText.textContent = line.textContent?.trim() ?? '';
    sheet.showModal();
  }

  #collapse() {
    this.#pinned = false;
    const { trigger, line } = this.#parts();
    if (!trigger || !line) return;
    line.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
  }

  /**
   * @returns {{
   *   trigger: HTMLElement | null,
   *   line: HTMLElement | null,
   *   sheet: HTMLDialogElement | null,
   *   close: HTMLElement | null,
   *   sheetText: HTMLElement | null,
   * }}
   */
  #parts() {
    const refs = /** @type {Record<string, any>} */ (this.refs ?? {});
    return {
      trigger: refs.trigger ?? null,
      line: refs.line ?? null,
      sheet: refs.sheet ?? null,
      close: refs.close ?? null,
      sheetText: refs.sheetText ?? null,
    };
  }
}

if (!customElements.get('echt-model-info')) {
  customElements.define('echt-model-info', EchtModelInfo);
}
